import { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import { FilterModel, RoomType, RoomTypeGrp } from 'ts/hotel/classes/FilterModel';
import FilterRoomType from './FilterRoomType';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { topBed, topCategories, topType } from 'constants/hotel';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface FilterByRoomAIProps {
  onChangeFilter: (filter: FilterModel) => void;
  onChangeRoom: (roomType: RoomTypeGrp) => void;
}

const FilterByRoomAI: FC<FilterByRoomAIProps> = ({ onChangeFilter, onChangeRoom }) => {
  const { t } = useTranslation();
  const { filter } = useContext(HotelFilterContext);
  const [freeTextSearch, setFreeTextSearch] = useState('');
  const [displayFilter, setDisplayFilter] = useState<RoomTypeGrp[] | undefined>(filter?.roomTypes);

  useEffect(() => {
    setDisplayFilter(filter?.roomTypes);
  }, [filter?.roomTypes]);

  const onClickClearAll = () => {
    if (filter?.roomTypes) {
      const newRoomTypes = filter.roomTypes.map((it) => {
        if (it.title === RoomType.ada) {
          return it;
        } else {
          return { ...it, options: it.options.map((opt) => ({ ...opt, checked: false })) };
        }
      });

      onChangeFilter({ ...filter, roomTypes: newRoomTypes, roomTypesArr: [] });
    }
  };

  const isClearVisible = (): boolean => {
    const roomTypes = filter?.roomTypes || [];
    return roomTypes
      .filter((it) => it.title !== RoomType.ada)
      .some((it) => it.options.some((opt) => opt.checked));
  };

  const onChangeFreeTextSearch = (text: string) => {
    setFreeTextSearch(text);

    const rt = filter?.roomTypes?.map((it) => {
      return {
        ...it,
        options: it.options.map((opt) => {
          return {
            ...opt,
            visible: opt.name?.toLowerCase().includes(text.toLowerCase()),
          };
        }),
      };
    });

    setDisplayFilter(rt);
  };

  const getType = (title: string): RoomTypeGrp | undefined => {
    const rt = displayFilter?.find((it) => it.title === title);
    const options = rt?.options.filter((it) => it.visible);

    return options && rt ? { ...rt, options } : undefined;
  };

  return (
    <div className="hotel-result-settings-content hotel-result-settings-roomai">
      <div className="flex-between">
        <h5 className="title">{t('hotel.filter_room_type')}</h5>
        {isClearVisible() && (
          <span
            className="text-normal text-light ml-10 color-danger cur-poi"
            onClick={onClickClearAll}
          >
            <span className="mr-5">{t('clear_all')}</span>
            <FontAwesomeIcon className="regular-svg" icon={faTimes} />
          </span>
        )}
      </div>

      <FormGroup
        label=""
        className="mb-10 display-ib movable-placeholder-xsm"
        movablePlaceholder={t('search.search') + '...'}
        onChange={onChangeFreeTextSearch}
        value={freeTextSearch}
        movablePlaceholderSm={true}
        movablePlaceholderIcon={faSearch}
      />
      <Fragment>
        {!!filter?.roomTypes && (
          <div className="flex-row pr-10 flex-between">
            <div className="flex-row w100p">
              <div className="col-md-6 col-12">
                <FilterRoomType type={getType(RoomType.vew)} onChange={onChangeRoom} />
                <FilterRoomType
                  type={getType(RoomType.ada)}
                  onChange={onChangeRoom}
                  top={topCategories}
                />
                <FilterRoomType type={getType(RoomType.blc)} onChange={onChangeRoom} />
                <FilterRoomType type={getType(RoomType.sgl)} onChange={onChangeRoom} />
                <FilterRoomType type={getType(RoomType.cat)} onChange={onChangeRoom} />
                <FilterRoomType type={getType(RoomType.cap)} onChange={onChangeRoom} />
              </div>
              <div className="col-md-6 col-12">
                <FilterRoomType
                  type={getType(RoomType.typ)}
                  onChange={onChangeRoom}
                  top={topType}
                />
                <FilterRoomType type={getType(RoomType.bdr)} onChange={onChangeRoom} />
                <FilterRoomType type={getType(RoomType.brd)} onChange={onChangeRoom} />
                <FilterRoomType type={getType(RoomType.pax)} onChange={onChangeRoom} />
                <FilterRoomType type={getType(RoomType.bed)} onChange={onChangeRoom} top={topBed} />
              </div>
            </div>
          </div>
        )}
      </Fragment>
    </div>
  );
};

export default withErrorBoundary(FilterByRoomAI, 'FilterByRoomAI');
